import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { SystemsService } from '@app/core/services/systems.service';
import { DocumentCollection } from 'ngx-jsonapi';
import { System } from '@app/core/resources/system';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { Transaction } from '@app/transactions/transaction';
import { SystemsUpdatedService } from '@app/transactions/services/systems-updated.service';
import { Observable, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { TransactionCalculatorComponent } from '@app/transactions/components/transaction-calculator/transaction-calculator.component';
import { HeaderService } from '@app/core/services/header-service';
import { CurrenciesService } from '@app/core/services/currencies.service';
import { PlatformBrowserService } from '@app/core/services/platform-browser.service';
import { CurrencyPiorityService } from '@app/core/services/currency-piority.service';
import { ReplaceSystemHelper } from '@app/core/helpers/replace-system.helper';
import { GroupHistoricalRate, GroupHistoricalRateService } from '@app/core/resources/group-historical-rate';
import { IChartSettings } from '@app/core/interfaces/chart-settings';
import { header_es_AR } from 'assets/i18n/header-es-ar';
import { TranslateService } from '@ngx-translate/core';
import { landing_es_AR } from 'assets/i18n/landing-es-ar';
import { exchange_es_AR } from 'assets/i18n/exchange-es-ar';
import { NetworkService } from '@app/core/services/network.service';

interface ILink {
    link: string;
    title: string;
}

@Component({
    selector: 'app-exchange',
    templateUrl: './exchange.component.html',
    styleUrls: ['./exchange.component.scss']
})
export class ExchangeComponent implements OnInit, OnDestroy {
    public systems: DocumentCollection<System> = new DocumentCollection();
    public systems$: Observable<DocumentCollection<System>> = new Observable();
    public sendSystem: System = new System();
    public receiveSystem: System = new System();
    public systemsSubscription: Subscription;
    public dataByChartsBid: any = [];
    public dataByChartsAsk: any = [];
    public comissionAux: any = [];
    public transaction: Transaction = new Transaction();
    public routeEventSubscription: Subscription = new Subscription();
    public darkTitle: string = 'Otras formas de comprar o vender saldo con ';
    public landingPageLink: string = '';
    public settingsChart: IChartSettings = {
        name: 'chartExchanges',
        chartData: [{ nameSeries: '', data: [''], color: 0x00cc99, gradient: true }],
        cursor: true,
        legend: true,
        tooltip: true
    };
    public section: string = '';
    public links: Array<ILink> = [
        {
            link: 'palpal-a-mercadopago',
            title: 'Comprar PayPal con Mercadopago'
        }
    ];
    @ViewChild(TransactionCalculatorComponent) public calculator!: TransactionCalculatorComponent;

    public constructor(
        public systemsService: SystemsService,
        public activatedRoute: ActivatedRoute,
        public router: Router,
        public groupHistoricalRateService: GroupHistoricalRateService,
        public currenciesService: CurrenciesService,
        public platformBrowserService: PlatformBrowserService,
        public changeDetectorRef: ChangeDetectorRef,
        public systemsUpdatedService: SystemsUpdatedService,
        private currencyPriorityService: CurrencyPiorityService,
        private translateService: TranslateService,
        public networksService: NetworkService,
        private headerService: HeaderService
    ) {
        currenciesService.register();
        this.routeEventSubscription = router.events.subscribe((event): void => {
            if (event instanceof NavigationEnd) {
                if (this.systems) {
                    this.setSystemsWithUrl();
                    this.transaction.attributes.amount1 = 0;
                    this.transaction.attributes.amount2 = 0;
                    this.systemsUpdatedService.emitSystemsUpdatedEvent(this.sendSystem, this.receiveSystem);
                }
            }
        });
        this.systemsSubscription = this.systemsUpdatedService.systemsUpdated$.pipe(debounceTime(100)).subscribe((systems): void => {
            this.setSystemsById(systems[0].id, systems[1].id);
            this.setTitleAndLinks();
            this.systemsUpdatedService.transactionUpdatedSource.next(this.transaction);
            this.updateLandingPageLink();
        });

        let langs: any = {
            ...header_es_AR,
            ...exchange_es_AR,
            ...landing_es_AR
        };
        this.translateService.setTranslation('exchange', langs, true);
        this.translateService.use('exchange');
    }

    public ngOnInit(): void {
        this.systems$ = this.systemsService.allIncludeRates();
        this.systems$.subscribe((systems): void => {
            this.refreshSystems(systems);
        });
    }

    public ngOnDestroy(): void {
        // this.transactionSubscription.unsubscribe();
        this.routeEventSubscription.unsubscribe();
    }

    public refreshSystems(systems: DocumentCollection<System>): void {
        this.systems = systems;
        this.setSystemsWithUrl();
        this.setUpTransaction();
        this.setTitleAndLinks();
        this.systemsUpdatedService.emitSystemsUpdatedEvent(this.sendSystem, this.receiveSystem);
    }

    public updateLandingPageLink(): void {
        this.landingPageLink =
            '/a/' +
            this.sendSystem.id +
            '/' +
            this.receiveSystem.id +
            '/' +
            this.transaction.attributes.amount1 +
            '/' +
            this.transaction.attributes.amount2;
    }

    private setUpTransaction(): void {
        this.transaction.attributes.system1 = this.sendSystem.id;
        this.transaction.attributes.system2 = this.receiveSystem.id;
        this.changeDetectorRef.detectChanges();
    }

    private setTitleAndLinks(): void {
        this.setTitle();
    }

    private setTitle(): void {
        this.currencyPriorityService.callbacksAndBreakByCurrency(
            this.sendSystem,
            this.receiveSystem,
            (): void => {
                this.darkTitle = this.sendSystem.attributes.alternative_name;
            },
            (): void => {
                this.darkTitle = this.receiveSystem.attributes.alternative_name;
            }
        );
    }

    private setSystemsWithUrl(): void {
        let actionArray: any = this.activatedRoute.snapshot.params.action.split('-');
        if (actionArray) {
            let replaceSystemHelper: ReplaceSystemHelper = new ReplaceSystemHelper(this.systems);
            let system1_param: any = replaceSystemHelper.getSystemOrReplacementSystem(actionArray[0] ?? 'banco');
            let system2_param: any = replaceSystemHelper.getSystemOrReplacementSystem(actionArray[2] ?? 'palpal');
            this.headerService.updateaSeoAndLanguageSettings('cobrar', system1_param, system2_param);
            this.setSystemsById(system1_param.id, system2_param.id);
        }
    }

    private setSystemsById(system1_id: string, system2_id: string): void {
        if (!this.platformBrowserService.isBrowser) {
            return;
        }
        let system1: System | null = this.systems.find(system1_id);
        let system2: System | null = this.systems.find(system2_id);
        if (system1 === null || system2 === null) {
            return;
        }
        this.sendSystem = system1;
        this.receiveSystem = system2;
        this.transaction.addRelationship(this.sendSystem, 'system1');
        this.transaction.addRelationship(this.receiveSystem, 'system2');
        /* eslint-disable id-blacklist */
        this.groupHistoricalRateService
            .all({
                remotefilter: { pair: this.currencyPriorityService.getPairByPriority(this.sendSystem, this.receiveSystem) },
                sort: ['-date'],
                page: { number: 1, size: 250 }
                /* eslint-enable id-blacklist */
            })
            .subscribe((groupHistoricalRates: DocumentCollection<GroupHistoricalRate>) => {
                this.dataByChartsBid = [];
                this.dataByChartsAsk = [];
                if (!groupHistoricalRates.builded) {
                    return;
                }
                groupHistoricalRates.data.forEach((groupHistoricalRate) => {
                    this.dataByChartsBid.push({
                        date: new Date(groupHistoricalRate.attributes.date).getTime(),
                        value: groupHistoricalRate.attributes.bid_price
                    });
                    this.dataByChartsAsk.push({
                        date: new Date(groupHistoricalRate.attributes.date).getTime(),
                        value: groupHistoricalRate.attributes.ask_price
                    });
                });
                this.dataByChartsBid = [].concat(this.dataByChartsBid);
                this.dataByChartsAsk = [].concat(this.dataByChartsAsk);
                this.settingsChart.chartData = [
                    {
                        nameSeries: 'Venta',
                        data: this.dataByChartsBid,
                        color: 0x00cc99,
                        gradient: true
                    },
                    {
                        nameSeries: 'Compra',
                        data: this.dataByChartsAsk,
                        color: 0x808080,
                        gradient: false
                    }
                ];
                this.settingsChart = { ...this.settingsChart };
                this.changeDetectorRef.markForCheck();
            });
    }
}
